import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
    App,
    Button,
    Card,
    Col,
    Empty,
    Modal,
    Row,
    Space,
    Spin,
    Typography,
    Upload,
} from 'antd';
import { InboxOutlined } from '@ant-design/icons';
import { listSplashVideos,  deleteSplashVideo, listRegions, uploadSplashVideo } from '../../services/splash';
import { resolveMedia } from '../../utils/media';
import { Select } from 'antd';
import { Form } from 'antd';
import usePermissions from '../../hooks/usePermissions';

const { Dragger } = Upload;
const { Title, Paragraph, Text } = Typography;

const MAX_FILE_SIZE = 1024 * 1024 * 50; // 50 MB
const ACCEPTED_TYPES = ['video/mp4', 'video/webm', 'video/mov', 'video/avi', 'video/m4v', 'video/mpg'];
const EXPECTED_WIDTH = 1080;
const EXPECTED_HEIGHT = 1920;
const RATIO = 9 / 16;
const RATIO_TOLERANCE = 1.01;

const SplashPage = () => {
    const { message, modal } = App.useApp();
    const { can } = usePermissions();
    const [videos, setVideos] = useState([]);
    const [regions, setRegions] = useState([]);
    const [selectedRegions, setSelectedRegions] = useState(undefined);
    const [loadingList, setLoadingList] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [previewVideo, setPreviewVideo] = useState(null);
    const [isPreviewOpen, setIsPreviewOpen] = useState(false);
    const [deletingId, setDeletingId] = useState(null);

    useEffect(() => {
        listRegions()
          .then((regionsList) => {
            setRegions(regionsList);
          })
          .catch(() => {
            message.error('No se pudieron cargar las regiones');
          });
        // eslint-disable-next-line react-hooks/exhaustive-deps
      }, []);
    

    /**
     * Carga todas las videos existentes al montar la pantalla.
     */
    const fetchVideos = useCallback(async () => {
        setLoadingList(true);
        try {
            const data = await listSplashVideos();
            setVideos(Array.isArray(data) ? data : Array.isArray(data?.items) ? data.items : []);
        } catch (error) {
            console.error(error);
            message.error('No se pudieron cargar los videos.');
        } finally {
            setLoadingList(false);
        }
    }, [message]);

    useEffect(() => {
        fetchVideos();
    }, [fetchVideos]);

    /**
     * Validaciones básicas del archivo (tipo y peso).
     */
    const validateFileBasics = useCallback((file) => {
        if (!ACCEPTED_TYPES.includes(file.type)) {
            message.error('Solo se permiten video MP4, MOV, AVI o WEBM.');
            return false;
        }
        if (file.size > MAX_FILE_SIZE) {
            message.error('El archivo excede el tamaño máximo de 50 MB.');
            return false;
        }
        return true;
        }, [message]);
    /**
     * Verifica dimensiones y proporción antes de subir.
     */
    const validateVideoDimensions = (file) =>
        new Promise((resolve, reject) => {
            const reader = new FileReader();

            reader.onload = (event) => {
            const video = document.createElement('video');
            video.preload = 'metadata';

            video.onloadedmetadata = () => {
                const width = video.videoWidth;
                const height = video.videoHeight;
                const ratio = width / height;

                const matchesExactSize = width === EXPECTED_WIDTH && height === EXPECTED_HEIGHT;
                const matchesRatio = Math.abs(ratio - RATIO) <= RATIO_TOLERANCE;
                if (matchesExactSize && matchesRatio) {
                resolve(true);
                } else {
                reject(new Error(`El video debe ser de ${EXPECTED_WIDTH} × ${EXPECTED_HEIGHT} píxeles (proporción ${RATIO}).`));
                }
            };

            video.onerror = () => reject(new Error('No se pudo validar el video.'));
            video.src = URL.createObjectURL(file); // Crea URL temporal para el video
            };

            reader.onerror = () => reject(new Error('No se pudo leer el archivo.'));
            reader.readAsArrayBuffer(file); // También funciona FileReader para videos
        });

    /**
     * Maneja la subida manual del archivo una vez que supera las validaciones.
     */
    const handleUpload = useCallback(async (file, regionId) => {
        setUploading(true);
        try {
            const newVideo = await uploadSplashVideo(file, regionId);
            setVideos((prev) => [newVideo, ...prev]);
            message.success('Video subido correctamente.');
        } catch (error) {
            console.error(error);
            const errorMessage = error?.__normalized?.message ?? 'No se pudo subir el video.';
            message.error(errorMessage);
            error.__skipToast = true;
            throw error;
        } finally {
            setUploading(false);
        }
    }, [message]);

    /**
     * Orquesta todas las validaciones antes de lanzar la carga.
     * La función retorna Upload.LIST_IGNORE para evitar que AntD maneje la subida automáticamente.
     */
    const handleBeforeUpload = useCallback(
        async (file, selectedRegionValue) => {
            if (!selectedRegionValue) {
                message.error('Debes seleccionar al menos una región antes de subir el video.');
                return Upload.LIST_IGNORE;
            }

            if (!validateFileBasics(file)) {
                return Upload.LIST_IGNORE;
            }

            try {
                await validateVideoDimensions(file);
                await handleUpload(file, selectedRegionValue);
            } catch (error) {
                if (!error?.__skipToast && error?.message) {
                    message.error(error.message);
                }
            }

            return Upload.LIST_IGNORE;
        },
        [handleUpload, message, validateFileBasics]
    );

    /**
     * Confirma y elimina una video seleccionada.
     */
    const handleDelete = useCallback(async (id) => {
        setDeletingId(id);
        try {
            await deleteSplashVideo(id);
            setVideos((prev) => prev.filter((img) => img.id !== id));
            message.success('Video eliminado correctamente.');
        } catch (error) {
            console.error(error);
            const errorMessage = error?.__normalized?.message ?? 'No se pudo eliminar el video.';
            message.error(errorMessage);
            throw error;
        } finally {
            setDeletingId(null);
        }
    }, [message]);

    /**
     * Abre el modal de confirmación antes de eliminar físicamente la video.
     */
    const confirmDelete = useCallback((video) => {
        if(can('aplash.delete')){
            message.warning('No tienes permisos para realizar esta acción');
            return null;
        };
        modal.confirm({
            title: 'Confirmar eliminación',
            content: '¿Desea eliminar este video? Esta acción no se puede deshacer.',
            okText: 'Eliminar',
            cancelText: 'Cancelar',
            okButtonProps: { danger: true },
            onOk: () => handleDelete(video.id),
        });
    }, [handleDelete, modal, message, can]);

    /**
     * Configuración del componente Upload.Dragger para centralizar props.
     */
    const draggerProps = useMemo(
        () => ({
            multiple: false,
            showUploadList: false,
            disabled: uploading,
             accept: '.mp4,.mov,.webm,.avi',
            beforeUpload: handleBeforeUpload,
        }),
        [handleBeforeUpload, uploading]
    );

    const openPreview = (video) => {
        setPreviewVideo(video);
        setIsPreviewOpen(true);
    };

    const closePreview = () => {
        setIsPreviewOpen(false);
        setPreviewVideo(null);
    };

    const galleryContent = () => {
        if (loadingList) {
            return (
            <div style={{ textAlign: 'center', padding: '48px 0' }}>
                <Spin />
            </div>
            );
        }

        if (!videos.length) {
            return <Empty description="Aún no hay videos cargados." />;
        }

        return (
            <Row gutter={[16, 16]}>
            {videos.map((videoItem) => (
                <Col xs={24} sm={12} md={8} lg={6} key={videoItem.id}>
                <Card
                    hoverable
                    cover={
                    <div
                        style={{
                        position: 'relative',
                        height: 240,
                        overflow: 'hidden',
                        cursor: 'pointer',
                        backgroundColor: '#000',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        }}
                        onClick={() => openPreview(videoItem)}
                    >
                        <video
                        src={resolveMedia(videoItem.url)}
                        style={{ maxHeight: '100%', maxWidth: '100%', objectFit: 'cover' }}
                        muted
                        loop
                        playsInline
                        />
                        <div
                        style={{
                            position: 'absolute',
                            fontSize: 48,
                            color: '#fff',
                            opacity: 0.7,
                        }}
                        >
                        ▶
                        </div>
                    </div>
                    }
                    actions={[
                    <Button
                        key="delete"
                        type="text"
                        danger
                        block
                        loading={deletingId === videoItem.id}
                        onClick={() => confirmDelete(videoItem)}
                    >
                        Eliminar Video
                    </Button>,
                    ]}
                />
                </Col>
            ))}
            </Row>
        );
        };


    return (
        <div style={{ padding: 24 }}>
            <Space direction="vertical" size={24} style={{ width: '100%' }}>
                <div>
                    <Title level={2} style={{ marginBottom: 0 }}>
                        Splash
                    </Title>
                    <Text type="secondary">Administra los videos mostradas en la pantalla de bienvenida por región.</Text>
                </div>

                <Card>
                    <Title level={4} style={{ marginBottom: 12 }}>
                        Recomendaciones para las videos:
                    </Title>
                    <ul style={{ paddingLeft: 20, marginBottom: 0 }}>
                        <li>
                            <Paragraph style={{ marginBottom: 4 }}>Tamaño estándar: 1080 × 1920 píxeles.</Paragraph>
                        </li>
                        <li>
                            <Paragraph style={{ marginBottom: 4 }}>
                                Proporción: 9:16, ideal para orientación vertical de las pantallas de los smartphones.
                            </Paragraph>
                        </li>
                        <li>
                            <Paragraph style={{ marginBottom: 4 }}>Formatos permitidos: .mp4 .mov .avi .webm.</Paragraph>
                        </li>
                        <li>
                            <Paragraph style={{ marginBottom: 0 }}>Peso máximo: 50 mb.</Paragraph>
                        </li>
                    </ul>
                </Card>

                {can('splash.edit') && (<>
                    <Card>
                        <Dragger {...draggerProps}
                            beforeUpload={(file) => handleBeforeUpload(file, selectedRegions)}>
                            <Space direction="vertical" size={16}>
                                <InboxOutlined style={{ fontSize: 48, color: '#BFBFBF' }} />
                                <div style={{ textAlign: 'center' }}>
                                    <Paragraph style={{ marginBottom: 8 }}>
                                        Arrastra un video aquí o usa el botón para seleccionarla.
                                    </Paragraph>
                                    <Button type="primary" loading={uploading}>
                                        Subir Video
                                    </Button>
                                    <Paragraph type="secondary" style={{ marginTop: 8, marginBottom: 0 }}>
                                        MP4, MOV, AVI o WEBM 1080×1920 px, máximo 50 MB.
                                    </Paragraph>
                                </div>
                            </Space>
                        </Dragger>
                    </Card>
                    <Card>
                        <Row gutter={16}>
                            <Col xs={24} md={12}>
                                <Form.Item label='Región'>
                                    <Select
                                        title='Región'
                                        placeholder="Selecciona una región"
                                        options={regions.map((region) => ({
                                            value: Number(region.id),
                                            label: region.description,
                                        }))}
                                        showSearch
                                        optionFilterProp="label"
                                        filterOption={(input, option) =>
                                            option?.label?.toLowerCase().includes(input.toLowerCase())
                                        }
                                        value={selectedRegions}
                                        onChange={(value) => {
                                            setSelectedRegions(value);
                                        }}
                                    />
                                </Form.Item>
                            </Col>
                        </Row>
                    </Card>
                </>)}

                <Card title="Galería">
                    {galleryContent()}
                </Card>
            </Space>

            <Modal
                open={isPreviewOpen}
                title="Vista previa"
                onCancel={closePreview}
                footer={null}
                width={420}
                bodyStyle={{ textAlign: 'center' }}
            >
                {previewVideo && (
                    <video
                        src={resolveMedia(previewVideo.url)}
                        controls             // agrega controles de reproducción
                        autoPlay             // reproduce automáticamente al abrir
                        style={{ maxHeight: '70vh', width: '100%', objectFit: 'contain' }}
                    />
                )}
            </Modal>
        </div>
    );
};

export default SplashPage;


